import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetUserByIdQuery,
  useDeleteUserByIdMutation,
  useUpdateUserProfileMutation,
  useGetUserStatsQuery,
  useGetUserSpaceStatsQuery,
  useGetUserBalanceQuery,
  useSetUserOfflineByAdminMutation,
  useAddNewLearningSpaceMutation,
  useDeleteLearningSpaceMutation,
} from "../services/user.Service";
import {
  useGetUserNotificationChannelsQuery,
} from "../services/notification-channels.service";
import { Spinner } from "../../../components/shared/atoms/spinner";
import { Badge } from "./UsersBadge";
import { UserStatistics } from "./UserStatistics";
import { UserAiPractice } from "./UserAiPractice";
import { UserBalanceDisplay } from "./UserBalanceDisplay";
import { UserFunctionErrorsSection } from "src/features/function-errors/components/UserFunctionErrorsSection";
import {
  FaArrowLeft,
  FaArrowUp,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaCalendar,
  FaUser,
  FaUserShield,
  FaClock,
  FaSyncAlt,
  FaReceipt,
  FaBook,
  FaChalkboardTeacher,
  FaVideo,
  FaCalendarAlt,
  FaCreditCard,
  FaBell,
  FaMobile,
  FaTablet,
  FaBroadcastTower,
  FaStar,
  FaCheckCircle,
  FaPlus,
  FaGraduationCap,
  FaBalanceScale,
  FaExclamationTriangle,
  FaWrench,
} from "react-icons/fa";
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from "sweetalert2";
import { getLangNameByLangCode, languageTuples } from "src/utils/util";
import { formatTimestamp } from "src/utils/dateHandler.util";
import Modal from "src/components/modal/modal";
import { getLearningSpaceErrorMessage } from "../constants/learning-space-errors";
import { useTheme } from "src/context/theme.context";
import { isUserSessionDisabled } from "src/utils/user-session.util";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";
import { NotificationHealthIssueBadge } from "src/features/notification-health/components/notification-health-issue-badge";
import {
  useRecheckNotificationHealthMutation,
  useRepairNotificationHealthMutation,
} from "src/features/notification-health/notification-health.service";
import {
  getNotificationHealthRepairActionLabel,
  getNotificationHealthRepairOutcomeDetails,
} from "src/features/notification-health/types/notification-health.entity";
import "./user-details-theme.css";

type UserDetailsSection = {
  id:
    | "overview"
    | "notifications"
    | "learning-tracks"
    | "statistics"
    | "ai-practice"
    | "function-errors"
    | "profile-info";
  label: string;
  learnerOnly?: boolean;
};

const USER_DETAILS_SECTIONS: readonly UserDetailsSection[] = [
  { id: "overview", label: "Overview" },
  { id: "notifications", label: "Notifications" },
  { id: "learning-tracks", label: "Learning Tracks", learnerOnly: true },
  { id: "statistics", label: "Statistics" },
  { id: "ai-practice", label: "AI Practice", learnerOnly: true },
  { id: "function-errors", label: "Function Errors" },
  { id: "profile-info", label: "Profile Info" },
] as const;

export const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backToUsers = appendSearchToPath("/users", location.search);
  const { isDarkMode } = useTheme();
  const { data: user, isLoading, isError, error, refetch, isFetching } = useGetUserByIdQuery(userId || "");
  const { data: userStats, isLoading: statsLoading, refetch: refetchStats } = useGetUserStatsQuery(userId || "");
  const {
    data: userSpaceStats,
    isLoading: userSpaceStatsLoading,
    refetch: refetchUserSpaceStats,
  } = useGetUserSpaceStatsQuery(userId || "");
  const {
    data: userBalance,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useGetUserBalanceQuery(userId || "");
  const {
    data: notificationChannels,
    isLoading: notificationLoading,
    refetch: refetchNotificationChannels,
  } = useGetUserNotificationChannelsQuery(userId || "");
  const [deleteUser] = useDeleteUserByIdMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [setUserOffline] = useSetUserOfflineByAdminMutation();
  const [addNewLearningSpace] = useAddNewLearningSpaceMutation();
  const [deleteLearningSpace] = useDeleteLearningSpaceMutation();
  const [
    recheckNotificationHealth,
    {
      data: recheckedNotificationHealth,
      isLoading: notificationRechecking,
      isError: notificationRecheckError,
      reset: resetNotificationRecheck,
    },
  ] = useRecheckNotificationHealthMutation();
  const [
    repairNotificationHealth,
    {
      data: repairedNotificationHealth,
      isLoading: notificationRepairing,
      isError: notificationRepairError,
      reset: resetNotificationRepair,
    },
  ] = useRepairNotificationHealthMutation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState<((typeof USER_DETAILS_SECTIONS)[number]["id"])>("overview");
  const [showAddLearningSpaceModal, setShowAddLearningSpaceModal] = useState(false);
  const [addLearningSpaceLangCode, setAddLearningSpaceLangCode] = useState("");
  const [addLearningSpaceLangLevel, setAddLearningSpaceLangLevel] = useState<string>("");
  const [addLearningSpaceSubmitting, setAddLearningSpaceSubmitting] = useState(false);
  const [deleteLearningSpaceSubmitting, setDeleteLearningSpaceSubmitting] = useState(false);

  // Handle scroll to top visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNotificationHealthRecheck = async () => {
    if (!userId) return;

    resetNotificationRepair();
    const result = await recheckNotificationHealth(userId);

    if ("data" in result) {
      await refetchNotificationChannels();
    }
  };

  const handleNotificationHealthRepair = async () => {
    if (!userId) return;

    const confirmation = await Swal.fire({
      title: "Repair notification routing?",
      text: "This will reconcile the user's stored routing and FCM topic subscriptions.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Repair routing",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d97706",
    });

    if (!confirmation.isConfirmed) return;

    resetNotificationRecheck();
    const result = await repairNotificationHealth(userId);

    if ("data" in result) {
      await refetchNotificationChannels();
    }
  };

  const notificationRepairOutcome = repairedNotificationHealth
    ? getNotificationHealthRepairOutcomeDetails(repairedNotificationHealth.outcome)
    : null;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [userId]);

  // Memoize role checks to prevent unnecessary re-renders
  const hasLearnerRole = useMemo(() => {
    return !!user?.roles?.some((role) => role.toLowerCase().includes("learner"));
  }, [user?.roles]);

  const hasCoachRole = useMemo(() => {
    return !!user?.roles?.some((role) => role.toLowerCase().includes("coach"));
  }, [user?.roles]);

  const navSections = useMemo(
    () =>
      USER_DETAILS_SECTIONS.map((section) => ({
        ...section,
        disabled: !!section.learnerOnly && !hasLearnerRole,
      })),
    [hasLearnerRole]
  );

  useEffect(() => {
    let ticking = false;

    const handleSectionTracking = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        const scrollPosition = window.scrollY + 180;
        let currentSection: UserDetailsSection["id"] = USER_DETAILS_SECTIONS[0].id;

        navSections.forEach(({ id, disabled }) => {
          if (disabled) return;
          const section = document.getElementById(id);
          if (section && section.offsetTop <= scrollPosition) {
            currentSection = id;
          }
        });

        setActiveSection((prev) => (prev === currentSection ? prev : currentSection));
        ticking = false;
      });
    };

    handleSectionTracking();
    window.addEventListener("scroll", handleSectionTracking, { passive: true });
    return () => window.removeEventListener("scroll", handleSectionTracking);
  }, [navSections]);

  // Memoized click handlers to prevent unnecessary re-renders


  // Enhanced refresh function that refreshes both user data and statistics
  const handleRefresh = async () => {
    try {
      await Promise.all([refetch(), refetchStats(), refetchUserSpaceStats(), refetchBalance()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const LANG_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;

  const handleAddLearningSpace = async () => {
    if (addLearningSpaceSubmitting) return;
    if (!user?.id || !addLearningSpaceLangCode.trim()) {
      Swal.fire({ icon: "warning", title: "Required", text: "Please select a language." });
      return;
    }
    setAddLearningSpaceSubmitting(true);
    try {
      const payload: { langCode: string; userId: string; langLevel?: (typeof LANG_LEVELS)[number] } = {
        langCode: addLearningSpaceLangCode.trim(),
        userId: user.id,
      };
      if (addLearningSpaceLangLevel && addLearningSpaceLangLevel.trim()) {
        payload.langLevel = addLearningSpaceLangLevel as (typeof LANG_LEVELS)[number];
      }
      Swal.fire({
        title: "Adding learning space...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await addNewLearningSpace(payload).unwrap();

      Swal.fire({ icon: "success", title: "Done", text: "Learning space added successfully." });
      setShowAddLearningSpaceModal(false);
      setAddLearningSpaceLangCode("");
      setAddLearningSpaceLangLevel("");
      refetch();
    } catch (err: any) {
      const msg = String(err?.data || err?.error || err?.message || "Failed to add learning space.");
      const looksNonFatal =
        /already\s*(exist|exists)|exists\s*already|duplicate|same\s*language/i.test(msg) ||
        /response\.data\s+is\s+null|can't\s+access\s+property\s+"success"/i.test(msg);
      if (looksNonFatal) {
        Swal.fire({ icon: "success", title: "Done", text: "Learning space already exists for this language." });
        setShowAddLearningSpaceModal(false);
        setAddLearningSpaceLangCode("");
        setAddLearningSpaceLangLevel("");
        refetch();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: getLearningSpaceErrorMessage(msg, "Failed to add learning space."),
        });
      }
    } finally {
      setAddLearningSpaceSubmitting(false);
    }
  };

  const handleDeleteLearningSpace = async () => {
    if (deleteLearningSpaceSubmitting || !user?.id) return;
    const result = await Swal.fire({
      title: "Delete learning space?",
      text: "This will remove the learning space for this user. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#e5e7eb",
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;
    setDeleteLearningSpaceSubmitting(true);
    try {
      Swal.fire({
        title: "Deleting learning space...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await deleteLearningSpace({ userId: user.id }).unwrap();
      Swal.fire({ icon: "success", title: "Done", text: "Learning space deleted successfully." });
      refetch();
    } catch (err: any) {
      const msg = String(err?.data || err?.error || err?.message || "Failed to delete learning space.");
      const looksNonFatal = /response\.data\s+is\s+null|can't\s+access\s+property\s+"success"/i.test(msg);
      if (looksNonFatal) {
        Swal.fire({ icon: "success", title: "Done", text: "Learning space has been removed." });
        refetch();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: getLearningSpaceErrorMessage(msg, "Failed to delete learning space."),
        });
      }
    } finally {
      setDeleteLearningSpaceSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">User Not Found</h2>
            <p className="text-red-600 mb-6">
              {error && typeof error === "object" && "data" in error
                ? String(error.data)
                : "The user you are looking for could not be found."}
            </p>
            <Link
              to={backToUsers}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium inline-block no-underline hover:no-underline"
            >
              Back to Users
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp.seconds * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRoleBadgeColor = (role: string) => {
    const roleColors: Record<string, string> = {
      ADMIN: "bg-green-100 text-green-800 border-green-200",
      admin: "bg-green-100 text-green-800 border-green-200",
      EMPLOYEE: "bg-blue-100 text-blue-800 border-blue-200",
      employee: "bg-blue-100 text-blue-800 border-blue-200",
      USER: "bg-gray-100 text-gray-800 border-gray-200",
      user: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return roleColors[role] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getActionLinkClass = (
    variant:
      | "blue"
      | "green"
      | "purple"
      | "orange"
      | "cyan"
      | "red"
      | "indigo"
      | "sky"
      | "amber"
      | "yellow"
  ) => {
    const darkVariants: Record<string, string> = {
      blue: "bg-blue-950/70 border border-blue-800/70 hover:bg-blue-900/80 hover:border-blue-700",
      green: "bg-emerald-950/70 border border-emerald-800/70 hover:bg-emerald-900/80 hover:border-emerald-700",
      purple: "bg-purple-950/70 border border-purple-800/70 hover:bg-purple-900/80 hover:border-purple-700",
      orange: "bg-orange-950/70 border border-orange-800/70 hover:bg-orange-900/80 hover:border-orange-700",
      cyan: "bg-cyan-950/70 border border-cyan-800/70 hover:bg-cyan-900/80 hover:border-cyan-700",
      red: "bg-red-950/70 border border-red-800/70 hover:bg-red-900/80 hover:border-red-700",
      indigo: "bg-indigo-950/70 border border-indigo-800/70 hover:bg-indigo-900/80 hover:border-indigo-700",
      sky: "bg-sky-950/70 border border-sky-800/70 hover:bg-sky-900/80 hover:border-sky-700",
      amber: "bg-amber-950/70 border border-amber-800/70 hover:bg-amber-900/80 hover:border-amber-700",
      yellow: "bg-yellow-950/70 border border-yellow-800/70 hover:bg-yellow-900/80 hover:border-yellow-700",
    };

    const lightVariants: Record<string, string> = {
      blue: "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
      green: "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700",
      purple: "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700",
      orange: "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700",
      cyan: "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700",
      red: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700",
      indigo: "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700",
      sky: "bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700",
      amber: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700",
      yellow: "bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700",
    };

    return `w-full px-6 py-4 rounded-2xl transition-all duration-300 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 no-underline hover:no-underline text-white ${
      isDarkMode ? darkVariants[variant] : lightVariants[variant]
    }`;
  };

  const getActionButtonClass = (variant: "indigo" | "red" | "orange") => {
    const darkVariants: Record<string, string> = {
      indigo: "bg-indigo-950/70 border border-indigo-800/70 hover:bg-indigo-900/80 hover:border-indigo-700",
      red: "bg-red-950/70 border border-red-800/70 hover:bg-red-900/80 hover:border-red-700",
      orange: "bg-orange-950/70 border border-orange-800/70 hover:bg-orange-900/80 hover:border-orange-700",
    };

    const lightVariants: Record<string, string> = {
      indigo: "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700",
      red: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700",
      orange: "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700",
    };

    return `px-6 py-3 rounded-2xl transition-all duration-300 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-white ${
      isDarkMode ? darkVariants[variant] : lightVariants[variant]
    }`;
  };

  const scrollToSection = (sectionId: (typeof USER_DETAILS_SECTIONS)[number]["id"]) => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const y = section.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top: y, behavior: "smooth" });
    setActiveSection(sectionId);
  };

  const isSessionDisabled = isUserSessionDisabled(user.appMeta);

  return (
    <div
      className={`user-details-page min-h-screen py-8 ${
        isDarkMode
          ? "user-details-dark bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900"
          : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Enhanced Header */}
          <div className="mb-10">
            <Link
              to={backToUsers}
              className="flex items-center text-emerald-600 hover:text-emerald-700 font-medium mb-6 transition-colors group no-underline hover:no-underline"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mr-3 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FaArrowLeft className="text-white" />
              </div>
              <span className="text-lg">Back to Users</span>
            </Link>

            <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
                    <FaUser className="text-white text-2xl" />
                  </div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-3">User Details</h1>
                  <p className="text-xl text-gray-600">Comprehensive user information and management</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Enhanced Refresh Button */}
                  <button
                    className={`${getActionButtonClass("indigo")} ${
                      isFetching || statsLoading || balanceLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                    onClick={handleRefresh}
                    disabled={isFetching || statsLoading || balanceLoading}
                  >
                    <FaSyncAlt
                      className={`mr-3 ${isFetching || statsLoading || balanceLoading ? "animate-spin" : ""}`}
                    />
                    Refresh Data
                  </button>

                  {/* Enhanced Delete Button */}
                  <button
                    className={getActionButtonClass("red")}
                    onClick={() => {
                      Swal.fire({
                        title: "Delete this user?",
                        text: "This action cannot be undone.",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#ef4444",
                        cancelButtonColor: "#e5e7eb",
                        confirmButtonText: "Yes, delete",
                      }).then(async (result: any) => {
                        if (result.isConfirmed) {
                          // Show loading while the delete operation is in progress
                          Swal.fire({
                            title: "Deleting user...",
                            allowOutsideClick: false,
                            didOpen: () => {
                              Swal.showLoading();
                            },
                          });

                          try {
                            await deleteUser(user.id).unwrap();
                            Swal.fire("Deleted!", "User has been deleted.", "success");
                            navigate(backToUsers);
                          } catch (error) {
                            await refetch();
                            Swal.fire("Error!", "Failed to delete user.", "error");
                          }
                        }
                      });
                    }}
                  >
                    <FaTrash className="mr-3" />
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="sticky top-4 z-30 mb-8">
            <div
              className={`overflow-x-auto rounded-2xl border shadow-lg backdrop-blur ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900/80"
                  : "border-white/70 bg-white/85"
              }`}
            >
              <div className="flex min-w-max gap-2 p-3">
                {navSections.map((section) => {
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => !section.disabled && scrollToSection(section.id)}
                      disabled={section.disabled}
                      className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                        isActive
                          ? isDarkMode
                            ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                            : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md"
                          : section.disabled
                            ? isDarkMode
                              ? "cursor-not-allowed text-gray-600 opacity-50"
                              : "cursor-not-allowed text-gray-400 opacity-60"
                          : isDarkMode
                            ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                            : "text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                      }`}
                    >
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Two Sections */}
            <div className="lg:col-span-1 space-y-8 animate__animated animate__fadeInLeft animate__fast">
              {/* First Section - Enhanced User Profile Card with Action Buttons */}
              <div id="overview" className="user-profile-section scroll-mt-36 bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="text-center mb-8">
                  {user.pictureURL ? (
                    <div className="relative">
                      <img
                        src={user.pictureURL}
                        alt="Profile"
                        className="w-28 h-28 rounded-full mx-auto mb-6 border-4 border-white shadow-xl"
                      />
                      <div
                        className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-4 border-white ${user.isOnline ? "bg-green-500" : "bg-gray-400"
                          }`}
                      ></div>
                    </div>
                  ) : (
                    <div className="w-28 h-28 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl">
                      <FaUser className="text-white text-4xl" />
                    </div>
                  )}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{user.name || "No Name"}</h2>
                  <p className="text-gray-600 text-lg">{user.email || "No Email"}</p>

                  {/* Status Badge */}
                  <div className="mt-4">
                    <div className="flex items-center justify-center gap-3">
                      <span
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${user.isOnline
                          ? "bg-green-100 text-green-800 border border-green-200"
                          : "bg-gray-100 text-gray-800 border border-gray-200"
                          }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full mr-2 ${user.isOnline ? "bg-green-500" : "bg-gray-500"}`}
                        ></div>
                        {user.isOnline ? "Online" : "Offline"}
                      </span>
                      {user.isOnline && (
                        <button
                          className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl transition-all duration-300 font-medium flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm"
                          onClick={() => {
                            Swal.fire({
                              title: "Set user offline?",
                              text: "This will set the user's status to offline.",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#f97316",
                              cancelButtonColor: "#e5e7eb",
                              confirmButtonText: "Yes, set offline",
                            }).then(async (result: any) => {
                              if (result.isConfirmed) {
                                // Show loading modal
                                Swal.fire({
                                  title: "Setting user offline...",
                                  allowOutsideClick: false,
                                  didOpen: () => {
                                    Swal.showLoading();
                                  },
                                });

                                try {
                                  await setUserOffline(user.id).unwrap();
                                  await handleRefresh();
                                  Swal.fire("Success", "User has been set to offline.", "success");
                                } catch (error) {
                                  Swal.fire(
                                    "Error",
                                    error instanceof Error ? error.message : "Failed to set user offline.",
                                    "error"
                                  );
                                }
                              }
                            });
                          }}
                        >
                          Set Offline
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Enhanced Quick Stats */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">

                    <span className="font-mono text-sm text-gray-800 bg-gray-200 px-2 py-1 rounded-lg mx-auto">{user.id}</span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                    <span className="text-gray-600 font-medium">Sessions</span>
                    <div className="flex items-center gap-2">
                      <Badge
                        label={isSessionDisabled ? "Disabled" : "Enabled"}
                        classes={
                          isSessionDisabled
                            ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                            : "bg-green-100 text-green-800 border-green-200"
                        }
                      />
                      {isSessionDisabled && (
                        <button
                          className="text-emerald-500 hover:text-emerald-600 transition-colors p-1 rounded-lg hover:bg-emerald-50"
                          onClick={() => {
                            Swal.fire({
                              title: "Enable sessions?",
                              text: "Are you sure you want to enable sessions for this user?",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#10b981",
                              cancelButtonColor: "#e5e7eb",
                              confirmButtonText: "Yes, enable",
                            }).then(async (result: any) => {
                              if (result.isConfirmed) {
                                // Show loading modal
                                Swal.fire({
                                  title: "Enabling sessions...",
                                  allowOutsideClick: false,
                                  didOpen: () => {
                                    Swal.showLoading();
                                  },
                                });

                                try {
                                  await updateUserProfile({ userId: user.id, field: "ENABLE_SESSIONS" }).unwrap();
                                  await refetch();
                                  Swal.fire("Success", "Sessions have been enabled for this user.", "success");
                                } catch (error) {
                                  Swal.fire("Error", "Failed to enable sessions.", "error");
                                }
                              }
                            });
                          }}
                        >
                          <FaEdit className="text-sm" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                    <span className="text-gray-600 font-medium">Ban Status</span>
                    <div className="flex items-center gap-3">
                      <Badge
                        label={user.isBanned ? "Banned" : "Not Banned"}
                        classes={
                          user.isBanned
                            ? "bg-red-100 text-red-800 border-red-200"
                            : "bg-green-100 text-green-800 border-green-200"
                        }
                      />
                      <button
                        type="button"
                        className={`relative inline-flex h-6 w-11 border border-gray-300 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${user.isBanned
                          ? "bg-red-500 border-red-500 focus:ring-red-300"
                          : "bg-gray-200 focus:ring-gray-300"
                          }`}
                        title={user.isBanned ? "Unban User" : "Ban User"}
                        onClick={() => {
                          Swal.fire({
                            title: user.isBanned ? "Unban this user?" : "Ban this user?",
                            text: user.isBanned
                              ? "This will allow the user to access the system again."
                              : "The user will be prevented from accessing the system.",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: user.isBanned ? "#10b981" : "#ef4444",
                            cancelButtonColor: "#e5e7eb",
                            confirmButtonText: user.isBanned ? "Yes, unban" : "Yes, ban",
                          }).then(async (result: any) => {
                            if (result.isConfirmed) {
                              Swal.fire({
                                title: user.isBanned ? "Unbanning user..." : "Banning user...",
                                allowOutsideClick: false,
                                didOpen: () => {
                                  Swal.showLoading();
                                },
                              });

                              try {
                                await updateUserProfile({
                                  userId: user.id,
                                  field: user.isBanned ? "UNBAN_USER" : "BAN_USER",
                                }).unwrap();
                                await refetch();
                                Swal.fire(
                                  "Success",
                                  user.isBanned ? "User has been unbanned." : "User has been banned.",
                                  "success"
                                );
                              } catch (error) {
                                Swal.fire("Error", "Failed to update user.", "error");
                              }
                            }
                          });
                        }}
                      >
                        <span
                          className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${user.isBanned ? "translate-x-5" : "translate-x-0"
                            }`}
                        />
                        <span className="sr-only">Toggle ban</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* User Transactions Button - Always show */}
                <div className="user-action-links mt-6">
                  <Link
                    to={`/users-transactions?user=${user?.id || ''}`}
                    className={getActionLinkClass("blue")}
                  >
                    <FaReceipt className="mr-3 text-lg flex-shrink-0" />
                    <span>View User Transactions</span>
                  </Link>
                </div>

                {/* User Bookings Button - Show if user has learner role */}
                {hasLearnerRole && (
                  <div className="user-action-links mt-4">
                    <Link
                      to={`/bookings?learnerId=${user?.id || ''}`}
                      className={getActionLinkClass("green")}
                    >
                      <FaBook className="mr-3 text-lg flex-shrink-0" />
                      <span>View Learner Bookings</span>
                    </Link>
                  </div>
                )}

                {/* Coach Bookings Button - Show if user has coach role */}
                {hasCoachRole && (
                  <div className="user-action-links mt-4">
                    <Link
                      to={`/bookings?coachId=${user?.id || ''}`}
                      className={getActionLinkClass("purple")}
                    >
                      <FaChalkboardTeacher className="mr-3 text-lg flex-shrink-0" />
                      <span>View Coach Bookings</span>
                    </Link>
                  </div>
                )}

                {/* Learner Sessions Button - Show if user has learner role */}
                {hasLearnerRole && (
                  <>
                    <div className="user-action-links mt-4">
                      <Link
                        to={`/sessions?learner=${user?.id || ''}`}
                        className={getActionLinkClass("orange")}
                      >
                        <FaVideo className="mr-3 text-lg flex-shrink-0" />
                        <span>View Learner Sessions</span>
                      </Link>
                    </div>
                    <div className="user-action-links mt-4">
                      <Link
                        to={`/slot-requests?learnerId=${user?.id || ''}`}
                        className={getActionLinkClass("cyan")}
                      >
                        <FaCalendarAlt className="mr-3 text-lg flex-shrink-0" />
                        <span>View Learner Slot Requests</span>
                      </Link>
                    </div>
                  </>
                )}

                {/* Learner AI Sessions Button - Show if user has learner role */}
                {hasLearnerRole && (
                  <div className="user-action-links mt-4">
                    <Link
                      to={`/ai-sessions?userId=${user?.id || ''}`}
                      className={getActionLinkClass("cyan")}
                    >
                      <FaVideo className="mr-3 text-lg flex-shrink-0" />
                      <span>View Learner AI Sessions</span>
                    </Link>
                  </div>
                )}

                {hasLearnerRole && (
                  <div className="user-action-links mt-4">
                    <Link
                      to={`/word-bank?userId=${user?.id || ''}`}
                      className={getActionLinkClass("yellow")}
                    >
                      <FaBook className="mr-3 text-lg flex-shrink-0" />
                      <span>View Learner Word Bank</span>
                    </Link>
                  </div>
                )}

                {/* Coach Sessions Button - Show if user has coach role */}
                {hasCoachRole && (
                  <>
                    <div className="user-action-links mt-4">
                      <Link
                        to={`/sessions?coach=${user?.id || ''}`}
                        className={getActionLinkClass("red")}
                      >
                        <FaVideo className="mr-3 text-lg flex-shrink-0" />
                        <span>View Coach Sessions</span>
                      </Link>
                    </div>
                    <div className="user-action-links mt-4">
                      <Link
                        to={`/slot-requests?coachId=${user?.id || ''}`}
                        className={getActionLinkClass("cyan")}
                      >
                        <FaCalendarAlt className="mr-3 text-lg flex-shrink-0" />
                        <span>View Coach Slot Requests</span>
                      </Link>
                    </div>
                  </>
                )}

                {/* Slots Button - Show if user has coach role */}
                {hasCoachRole && (
                  <div className="user-action-links mt-4">
                    <Link
                      to={`/slots?userId=${user?.id || ''}`}
                      className={getActionLinkClass("indigo")}
                    >
                      <FaCalendarAlt className="mr-3 text-lg flex-shrink-0" />
                      <span>View Slots</span>
                    </Link>
                  </div>
                )}

                {/* User Rooms Button - Always show */}
                <div className="user-action-links mt-4">
                  <Link
                    to={`/rooms?userId=${user?.id || ''}`}
                    className={getActionLinkClass("sky")}
                  >
                    <FaBroadcastTower className="mr-3 text-lg flex-shrink-0" />
                    <span>View User Rooms</span>
                  </Link>
                </div>

                {/* Payment Transactions Button - Always show */}
                <div className="user-action-links mt-4">
                  <Link
                    to={`/payment-transactions?userId=${user?.id || ''}`}
                    className={getActionLinkClass("amber")}
                  >
                    <FaCreditCard className="mr-3 text-lg flex-shrink-0" />
                    <span>View Payment Transactions</span>
                  </Link>
                </div>

                {/* Subscription Users Button - Always show */}
                <div className="user-action-links mt-4">
                  <Link
                    to={`/subscription-users/${user?.id || ''}`}
                    className={getActionLinkClass("yellow")}
                  >
                    <FaStar className="mr-3 text-lg flex-shrink-0" />
                    <span>View User Subscriptions</span>
                  </Link>
                </div>

                {/* Redeem Voucher Button */}
                <div className="user-action-links mt-4">
                  <Link
                    to={`/credit-vouchers/redeem/${user?.id || ''}`}
                    className={getActionLinkClass("green")}
                  >
                    <FaCheckCircle className="mr-3 text-lg flex-shrink-0" />
                    <span>Redeem Voucher</span>
                  </Link>
                </div>
              </div>

              {/* Second Section - FCM Tokens and Subscribed Channels */}
              <div id="notifications" className="space-y-6 scroll-mt-36">
                {/* FCM Tokens Section */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 border border-gray-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mr-3 flex items-center justify-center">
                      <FaMobile className="text-white text-lg" />
                    </div>
                    FCM Tokens
                  </h3>

                  {notificationLoading ? (
                    <div className="flex items-center justify-center p-4">
                      <FaSyncAlt className="animate-spin h-5 w-5 text-blue-500 mr-2" />
                      <span className="text-gray-600">Loading tokens...</span>
                    </div>
                  ) : notificationChannels?.fcmTokens?.tokens?.length ? (
                    <div className="space-y-3">
                      {notificationChannels.fcmTokens.tokens.map((token, index) => (
                        <div key={index} className="border border-gray-200 rounded-2xl p-4 bg-white">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center ${token.platform === "IOS" ? "bg-blue-100" : "bg-green-100"
                                  }`}
                              >
                                {token.platform === "IOS" ? (
                                  <FaTablet className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <FaMobile className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${token.platform === "IOS" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                                  }`}
                              >
                                {token.platform}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">{formatTimestamp(token.createdAt)}</span>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <code className="text-sm font-mono bg-gray-100 px-3 py-2 rounded-lg block w-full break-all">{token.token}</code>
                            </div>

                            {token.apnId && (
                              <div>
                                <span className="text-xs text-gray-500 font-medium block mb-1">APN ID:</span>
                                <code className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border text-gray-700 block w-full break-all">{token.apnId}</code>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic text-center py-4">No FCM tokens found</p>
                  )}
                </div>

                {/* Notification Routing Section */}
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 border border-gray-100">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <h3 className="flex items-center text-xl font-semibold text-gray-900">
                      <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600">
                        <FaBell className="text-lg text-white" />
                      </div>
                      Notification Routing
                    </h3>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={handleNotificationHealthRepair}
                        disabled={!userId || notificationRechecking || notificationRepairing}
                        title="Repair notification routing"
                        aria-label="Repair notification routing"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-800 transition-colors hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaWrench className={notificationRepairing ? "animate-pulse" : ""} />
                      </button>
                      <button
                        type="button"
                        onClick={handleNotificationHealthRecheck}
                        disabled={!userId || notificationRechecking || notificationRepairing}
                        title="Recheck notification routing"
                        aria-label="Recheck notification routing"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-sky-200 bg-sky-50 text-sky-700 transition-colors hover:bg-sky-100 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaSyncAlt className={notificationRechecking ? "animate-spin" : ""} />
                      </button>
                    </div>
                  </div>

                  {repairedNotificationHealth && notificationRepairOutcome ? (
                    <div className={`mb-5 border p-4 ${notificationRepairOutcome.className}`}>
                      <div className="text-sm font-semibold">
                        {notificationRepairOutcome.label}
                      </div>
                      <p className="mt-1 text-xs leading-5 opacity-90">
                        {notificationRepairOutcome.description}
                      </p>
                      {repairedNotificationHealth.actions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {repairedNotificationHealth.actions.map((action) => (
                            <span
                              key={action}
                              className="border border-current/20 bg-white/50 px-2 py-1 text-[10px] font-semibold"
                            >
                              {getNotificationHealthRepairActionLabel(action)}
                            </span>
                          ))}
                        </div>
                      )}
                      {repairedNotificationHealth.issueCodes.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {repairedNotificationHealth.issueCodes.map((issueCode) => (
                            <NotificationHealthIssueBadge
                              key={issueCode}
                              issueCode={issueCode}
                            />
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-90">
                        <span>Tokens: {repairedNotificationHealth.tokenCount}</span>
                        <span>Stored: {repairedNotificationHealth.storedTopicCount}</span>
                        <span>Recorded FCM: {repairedNotificationHealth.recordedFcmTopicCount}</span>
                        <span>Expected stored: {repairedNotificationHealth.expectedStoredTopicCount}</span>
                      </div>
                    </div>
                  ) : notificationRepairError ? (
                    <div className="mb-5 border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                      Notification routing could not be repaired. Please try again.
                    </div>
                  ) : recheckedNotificationHealth ? (
                    <div
                      className={`mb-5 border p-4 ${
                        recheckedNotificationHealth.issueCodes.length
                          ? "border-amber-200 bg-amber-50"
                          : "border-emerald-200 bg-emerald-50"
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${
                          recheckedNotificationHealth.issueCodes.length
                            ? "text-amber-900"
                            : "text-emerald-800"
                        }`}
                      >
                        {recheckedNotificationHealth.issueCodes.length
                          ? "Current exceptions"
                          : "No current notification-routing exceptions"}
                      </div>
                      {recheckedNotificationHealth.issueCodes.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {recheckedNotificationHealth.issueCodes.map((issueCode) => (
                            <NotificationHealthIssueBadge
                              key={issueCode}
                              issueCode={issueCode}
                            />
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                        <span>Tokens: {recheckedNotificationHealth.tokenCount}</span>
                        <span>Stored: {recheckedNotificationHealth.storedTopicCount}</span>
                        <span>Recorded FCM: {recheckedNotificationHealth.recordedFcmTopicCount}</span>
                        <span>Expected stored: {recheckedNotificationHealth.expectedStoredTopicCount}</span>
                      </div>
                    </div>
                  ) : notificationRecheckError ? (
                    <div className="mb-5 border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                      Notification routing could not be rechecked. Please try again.
                    </div>
                  ) : notificationChannels?.notificationHealth ? (
                    <div className="mb-5 border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <FaExclamationTriangle className="mt-0.5 text-amber-600" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-amber-900">Needs attention</div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {notificationChannels.notificationHealth.issueCodes.map((issueCode) => (
                              <NotificationHealthIssueBadge
                                key={issueCode}
                                issueCode={issueCode}
                              />
                            ))}
                          </div>
                          <div className="mt-3 text-xs text-amber-800">
                            First detected: {formatTimestamp(notificationChannels.notificationHealth.firstDetectedAt)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : !notificationLoading ? (
                    <div className="mb-5 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
                      No current notification-routing exceptions.
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 gap-6">
                    {/* Stored Broadcast Topics */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <FaBroadcastTower className="w-4 h-4 mr-2 text-green-500" />
                        Stored Broadcast Topics
                      </h4>
                      {notificationChannels?.subscribedChannels?.fcmTopics?.length ? (
                        <div className="space-y-2">
                          {notificationChannels.subscribedChannels.fcmTopics.map((topic, index) => (
                            <div key={index} className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                              <span className="text-sm text-green-800 font-medium">{topic}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm italic">No stored broadcast topics</p>
                      )}
                    </div>

                    {/* Recorded FCM Topics */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <FaBell className="w-4 h-4 mr-2 text-sky-500" />
                        Recorded FCM Topics
                      </h4>
                      {notificationChannels?.subscribedChannels?.subscribedTopics?.length ? (
                        <div className="space-y-2">
                          {notificationChannels.subscribedChannels.subscribedTopics.map((topic, index) => (
                            <div key={index} className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3">
                              <span className="text-sm text-sky-800 font-medium">{topic}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm italic">No recorded FCM topics</p>
                      )}
                    </div>

                    {/* Custom Broadcast Channels */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <FaBell className="w-4 h-4 mr-2 text-purple-500" />
                        Custom Channels
                      </h4>
                      {notificationChannels?.subscribedChannels?.customBroadcastChannels?.length ? (
                        <div className="space-y-2">
                          {notificationChannels.subscribedChannels.customBroadcastChannels.map((channel, index) => (
                            <div key={index} className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
                              <span className="text-sm text-purple-800 font-medium">{channel}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm italic">No custom channels subscribed</p>
                      )}
                    </div>
                  </div>

                  {/* Last Updated */}
                  {notificationChannels?.subscribedChannels?.updatedAt && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500">Last updated: {formatTimestamp(notificationChannels.subscribedChannels.updatedAt)}</p>
                    </div>
                  )}
                </div>

                {/* Create Bookings Section - Only for Learners */}
                {hasLearnerRole && user?.id && (
                  <div id="learning-tracks" className="scroll-mt-36 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl shadow-2xl p-6 border border-emerald-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">

                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">Create Bookings</h3>
                          <p className="text-sm text-gray-600">Schedule sessions</p>
                        </div>
                      </div>
                      <Link
                        to={`/users/${user.id}/create-bookings`}
                        className={`${getActionLinkClass("green")} px-6 py-3 rounded-xl hover:scale-105`}
                      >
                        <FaCalendarAlt className="text-sm" />
                        Create Bookings
                      </Link>
                    </div>
                  </div>
                )}

                {/* Add Learning Space Section */}
                {hasLearnerRole && user?.id && (
                  <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-3xl shadow-2xl p-6 border border-violet-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">Add Learning Space</h3>
                          <p className="text-sm text-gray-600">Create a new learning space for this user</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddLearningSpaceModal(true)}
                        className="px-6 py-3 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                      >
                        <FaPlus className="text-sm" />
                        Add Learning Space
                      </button>
                    </div>
                  </div>
                )}

                {/* Delete Learning Space Section - Only for users with both coach and learner roles */}
                {hasCoachRole && hasLearnerRole && user?.id && (
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl shadow-2xl p-6 border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">Delete Learning Space</h3>
                          <p className="text-sm text-gray-600">Remove the learning space for this user</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleDeleteLearningSpace}
                        disabled={deleteLearningSpaceSubmitting}
                        className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        <FaTrash className="text-sm" />
                        {deleteLearningSpaceSubmitting ? "Deleting…" : "Delete Learning Space"}
                      </button>
                    </div>
                  </div>
                )}

                {hasLearnerRole && user?.id && (
                  <div className="relative overflow-hidden rounded-3xl border border-sky-200 bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-100 p-6 shadow-2xl">
                    <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/35 blur-2xl" />
                    <div className="absolute bottom-0 left-0 h-24 w-24 rounded-full bg-cyan-300/20 blur-2xl" />

                    <div className="relative">
                      <div className="mb-5 flex items-start gap-4">
                        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 text-white shadow-lg shadow-sky-500/25">
                          <FaGraduationCap className="text-lg" />
                        </div>

                        <div className="min-w-0">
                          <div className="mb-2 inline-flex items-center rounded-full border border-sky-200 bg-white/75 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">
                            Learner Journey
                          </div>
                          <h3 className="mb-2 text-3xl font-bold leading-tight text-slate-900">Learner Track Progress</h3>
                          <p className="text-sm leading-6 text-slate-600">
                            Review the learner&apos;s current point, completed topics, and full track details from one dedicated page.
                          </p>
                        </div>
                      </div>

                      <div className="mb-5 rounded-2xl border border-white/80 bg-white/70 px-4 py-3 text-sm text-slate-600 shadow-sm backdrop-blur">
                        Shows started tracks first, keeps non-started tracks collapsed, and helps you jump quickly to the learner&apos;s current position.
                      </div>

                      <Link
                        to={`/users/${user.id}/progress`}
                        className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-sky-500 via-cyan-500 to-blue-600 px-6 py-4 text-base font-semibold text-white no-underline shadow-lg shadow-sky-500/30 transition-all duration-200 hover:-translate-y-1 hover:from-sky-600 hover:via-cyan-600 hover:to-blue-700 hover:no-underline hover:shadow-xl"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/20">
                          <FaGraduationCap className="text-sm" />
                        </span>
                        <span>Open Learner Progress</span>
                      </Link>
                    </div>
                  </div>
                )}

                {user?.id && (
                  <div className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-5 shadow-xl">
                    <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/35 blur-2xl" />
                    <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-emerald-300/20 blur-2xl" />

                    <div className="relative">
                      <div className="mb-4 flex items-start gap-3">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-600 text-white shadow-lg shadow-emerald-500/25">
                          <FaBalanceScale className="text-base" />
                        </div>

                        <div className="min-w-0">
                          <div className="mb-2 inline-flex items-center rounded-full border border-emerald-200 bg-white/75 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                            Meeting Reliability
                          </div>
                          <h3 className="mb-1 text-2xl font-bold leading-tight text-slate-900">Meeting Policy</h3>
                          <p className="text-sm leading-5 text-slate-600">
                            Review learner and coach reliability, penalties, restrictions, and policy events.
                          </p>
                        </div>
                      </div>

                      <Link
                        to={`/users/${user.id}/meeting-policy`}
                        className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-emerald-500 via-cyan-500 to-teal-600 px-5 py-3.5 text-sm font-semibold text-white no-underline shadow-lg shadow-emerald-500/30 transition-all duration-200 hover:-translate-y-1 hover:from-emerald-600 hover:via-cyan-600 hover:to-teal-700 hover:no-underline hover:shadow-xl"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20">
                          <FaBalanceScale className="text-sm" />
                        </span>
                        <span>Open Meeting Policy</span>
                      </Link>
                    </div>
                  </div>
                )}

                {user?.id && (
                  <div className="relative overflow-hidden rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-100 p-5 shadow-xl">
                    <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/35 blur-2xl" />
                    <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-violet-300/20 blur-2xl" />

                    <div className="relative">
                      <div className="mb-4 flex items-start gap-3">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/25">
                          <FaUserShield className="text-base" />
                        </div>

                        <div className="min-w-0">
                          <div className="mb-2 inline-flex items-center rounded-full border border-violet-200 bg-white/75 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-700">
                            Safety & Privacy
                          </div>
                          <h3 className="mb-1 text-2xl font-bold leading-tight text-slate-900">User Blocks</h3>
                          <p className="text-sm leading-5 text-slate-600">
                            Check blocked users and users who blocked them.
                          </p>
                        </div>
                      </div>

                      <Link
                        to={`/users/${user.id}/blocks`}
                        className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-600 px-5 py-3.5 text-sm font-semibold text-white no-underline shadow-lg shadow-violet-500/30 transition-all duration-200 hover:-translate-y-1 hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-700 hover:no-underline hover:shadow-xl"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20">
                          <FaUserShield className="text-sm" />
                        </span>
                        <span>Open User Blocks</span>
                      </Link>
                    </div>
                  </div>
                )}

                {user?.id && (
                  <div className="relative overflow-hidden rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-100 p-5 shadow-xl">
                    <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-white/35 blur-2xl" />
                    <div className="absolute bottom-0 left-0 h-20 w-20 rounded-full bg-cyan-300/20 blur-2xl" />

                    <div className="relative">
                      <div className="mb-4 flex items-start gap-3">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25">
                          <FaSyncAlt className="text-base" />
                        </div>

                        <div className="min-w-0">
                          <div className="mb-2 inline-flex items-center rounded-full border border-cyan-200 bg-white/75 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-700">
                            Activity History
                          </div>
                          <h3 className="mb-1 text-2xl font-bold leading-tight text-slate-900">Space Switch History</h3>
                          <p className="text-sm leading-5 text-slate-600">
                            Review target space changes plus the latest learning and coaching switch dates.
                          </p>
                        </div>
                      </div>

                      <Link
                        to={`/users/${user.id}/space-switch-history`}
                        className="inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-600 px-5 py-3.5 text-sm font-semibold text-white no-underline shadow-lg shadow-cyan-500/30 transition-all duration-200 hover:-translate-y-1 hover:from-cyan-600 hover:via-sky-600 hover:to-blue-700 hover:no-underline hover:shadow-xl"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20">
                          <FaSyncAlt className="text-sm" />
                        </span>
                        <span>Open Space Switch History</span>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Enhanced Detailed Information */}
            <div className="lg:col-span-2 space-y-8 animate__animated animate__fadeInRight animate__fast">
              {/* Enhanced Personal Information */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl mr-4 flex items-center justify-center">
                    <FaUser className="text-white text-lg" />
                  </div>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Full Name</label>
                    <p className="text-gray-900 font-semibold text-lg">{user.name || "Not provided"}</p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Email Address</label>
                    <p className="text-gray-900 font-semibold text-lg flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg mr-3 flex items-center justify-center">
                        <FaEnvelope className="text-white text-sm" />
                      </div>
                      {user.email || "Not provided"}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Phone Number</label>
                    <p className="text-gray-900 font-semibold text-lg flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-3 flex items-center justify-center">
                        <FaPhone className="text-white text-sm" />
                      </div>
                      {user.appProfile?.phoneNumber || "Not provided"}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Created Date</label>
                    <p className="text-gray-900 font-semibold text-lg flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-3 flex items-center justify-center">
                        <FaCalendar className="text-white text-sm" />
                      </div>
                      {formatDate(user.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Roles & Permissions */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mr-4 flex items-center justify-center">
                    <FaUserShield className="text-white text-lg" />
                  </div>
                  Roles & Permissions
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4 block">
                      User Roles
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role, index) => (
                          <Badge key={index} label={role} classes={getRoleBadgeColor(role)} />
                        ))
                      ) : (
                        <span className="text-gray-500 text-lg">No roles assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Account Information */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl mr-4 flex items-center justify-center">
                    <FaClock className="text-white text-lg" />
                  </div>
                  Account Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Login</label>
                    <p className="text-gray-900 font-semibold text-lg">
                      {user.lastLogin ? formatDate(user.lastLogin) : "N/A"}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Seen</label>
                    <p className="text-gray-900 font-semibold text-lg">
                      {user.lastSeenAt ? formatDate(user.lastSeenAt) : "N/A"}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Last Updated</label>
                    <p className="text-gray-900 font-semibold text-lg">{formatDate(user.updatedAt)}</p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-500 uppercase tracking-wide">Time Zone</label>
                    <p className="text-gray-900 font-semibold text-lg">{user.timeZone || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Enhanced Notification Preferences */}
              {user.appNotificationPreferences && (
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl mr-4 flex items-center justify-center">
                      <FaEnvelope className="text-white text-lg" />
                    </div>
                    Notification Preferences
                  </h3>
                  <div className="space-y-6">
                    {/* Pause Notifications */}
                    {user.appNotificationPreferences.pauseNotification && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Pause Notifications</span>
                        <div className="flex items-center gap-3 mt-2 md:mt-0">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${user.appNotificationPreferences.pauseNotification.isEnabled
                              ? "bg-red-100 text-red-800 border border-red-200"
                              : "bg-green-100 text-green-800 border border-green-200"
                              }`}
                          >
                            {user.appNotificationPreferences.pauseNotification.isEnabled ? "Enabled" : "Disabled"}
                          </span>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${user.appNotificationPreferences.pauseNotification.allowCriticalNotification
                              ? "bg-blue-100 text-blue-800 border border-blue-200"
                              : "bg-gray-100 text-gray-800 border border-gray-200"
                              }`}
                          >
                            Critical:{" "}
                            {user.appNotificationPreferences.pauseNotification.allowCriticalNotification ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Do Not Disturb */}
                    {user.appNotificationPreferences.doNotDisturb && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Do Not Disturb</span>
                        <div className="flex items-center gap-3 mt-2 md:mt-0">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${user.appNotificationPreferences.doNotDisturb.isEnabled
                              ? "bg-red-100 text-red-800 border border-red-200"
                              : "bg-green-100 text-green-800 border border-green-200"
                              }`}
                          >
                            {user.appNotificationPreferences.doNotDisturb.isEnabled ? "Enabled" : "Disabled"}
                          </span>
                          {user.appNotificationPreferences.doNotDisturb.startTime && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 border border-purple-200 rounded-full text-sm font-medium">
                              {user.appNotificationPreferences.doNotDisturb.startTime} -{" "}
                              {user.appNotificationPreferences.doNotDisturb.endTime}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Session Reminders */}
                    {user.appNotificationPreferences.sessionReminders && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Session Reminders</span>
                        <div className="flex items-center gap-3 mt-2 md:mt-0">
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${user.appNotificationPreferences.sessionReminders.isEnabled
                              ? "bg-green-100 text-green-800 border border-green-200"
                              : "bg-red-100 text-red-800 border border-red-200"
                              }`}
                          >
                            {user.appNotificationPreferences.sessionReminders.isEnabled ? "Enabled" : "Disabled"}
                          </span>
                          {user.appNotificationPreferences.sessionReminders.reminderTimes && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-sm font-medium">
                              Times: {user.appNotificationPreferences.sessionReminders.reminderTimes.join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Available Slots Broadcast */}
                    {user.appNotificationPreferences.availableSlots && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Available Slots Alerts</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.availableSlots.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.availableSlots.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}

                    {/* Learner Lounge */}
                    {user.appNotificationPreferences.learnerLounge && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Learner Lounge</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.learnerLounge.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.learnerLounge.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}

                    {/* Coach Lounge */}
                    {user.appNotificationPreferences.coachLounge && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Coach Lounge</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.coachLounge.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.coachLounge.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}

                    {/* Feed Activity */}
                    {user.appNotificationPreferences.feedActivity && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Feed Activity</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.feedActivity.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.feedActivity.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}

                    {/* Chats */}
                    {user.appNotificationPreferences.chats && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Chats</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.chats.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.chats.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}

                    {/* New AI Coach */}
                    {user.appNotificationPreferences.newAiCoach && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">New AI Coach</span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium mt-2 md:mt-0 ${user.appNotificationPreferences.newAiCoach.isEnabled
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                        >
                          {user.appNotificationPreferences.newAiCoach.isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Enhanced Application Settings */}
              {user.appSettings && user.roles && user.roles.includes("COACH") && (
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mr-4 flex items-center justify-center">
                      <FaUserShield className="text-white text-lg" />
                    </div>
                    Application Settings
                  </h3>
                  <div className="space-y-6">
                    {/* Coach Booking Approval */}
                    {user.appSettings.coachSettings?.bookingApproval && (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium">Booking Approval</span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-sm font-medium mt-2 md:mt-0 capitalize">
                          {user.appSettings.coachSettings.bookingApproval}
                        </span>
                      </div>
                    )}

                    {/* Learner Settings */}
                    {user.appSettings.learnerSettings && Object.keys(user.appSettings.learnerSettings).length > 0 && (
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                        <span className="text-gray-700 font-medium mb-3 block">Learner Settings</span>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-white p-3 rounded-lg border">
                          {JSON.stringify(user.appSettings.learnerSettings, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* User Statistics */}
              <div id="statistics" className="scroll-mt-36">
                <UserStatistics
                  stats={userStats || null}
                  spaceStats={userSpaceStats || []}
                  isLoading={statsLoading || userSpaceStatsLoading}
                />
              </div>

              {hasLearnerRole && userId && (
                <div id="ai-practice" className="scroll-mt-36">
                  <UserAiPractice userId={userId} />
                </div>
              )}

              {/* Balance Section */}
              <UserBalanceDisplay balance={userBalance || null} isLoading={balanceLoading} />

              {/* Cloud function errors */}
              {userId && (
                <div id="function-errors" className="scroll-mt-36">
                  <UserFunctionErrorsSection
                    userId={userId}
                    showUserLink={false}
                    messageModalPosition="absolute"
                  />
                </div>
              )}

              {/* Enhanced Additional Profile Information */}
              {user.appProfile && (
                <div id="profile-info" className="scroll-mt-36 bg-gradient-to-br from-indigo-50 to-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                  <h3 className="text-3xl font-bold text-emerald-700 mb-8 flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-400 rounded-full mr-3"></div>
                    Additional Profile Information
                  </h3>

                  {/* App Meta Information Section */}
                  {user.appMeta && (
                    <div className="mb-8">
                      <h4 className="text-2xl font-semibold text-indigo-700 mb-6 flex items-center gap-3">
                        <div className="w-6 h-6 bg-indigo-400 rounded-full"></div>
                        Application Meta Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Active Learning Language */}
                        {user.appMeta.activeLearningLangCode && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 5.477 5.754 5 7.5 5s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.523 18.246 19 16.5 19c-1.746 0-3.332-.477-4.5-1.253" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-blue-700 uppercase tracking-wide">
                                Active Learning Language
                              </span>
                              <div className="text-lg font-bold text-blue-900">
                                {getLangNameByLangCode(user.appMeta.activeLearningLangCode)}
                              </div>
                              <div className="text-sm text-blue-600 font-mono">
                                {user.appMeta.activeLearningLangCode}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Active Coaching Language */}
                        {user.appMeta.activeCoachingLangCode && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-2xl border border-green-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-green-700 uppercase tracking-wide">
                                Active Coaching Language
                              </span>
                              <div className="text-lg font-bold text-green-900">
                                {getLangNameByLangCode(user.appMeta.activeCoachingLangCode)}
                              </div>
                              <div className="text-sm text-green-600 font-mono">
                                {user.appMeta.activeCoachingLangCode}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Learning Level */}
                        {user.appMeta.activeLearningLangLevel && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl border border-purple-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-purple-700 uppercase tracking-wide">
                                Learning Level
                              </span>
                              <div className="text-lg font-bold text-purple-900">
                                {user.appMeta.activeLearningLangLevel}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Coach Type */}
                        {user.appMeta.coachType && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl border border-orange-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="M16 7a4 4 0 01-8 0" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-orange-700 uppercase tracking-wide">
                                Coach Type
                              </span>
                              <div className="text-lg font-bold text-orange-900">{user.appMeta.coachType}</div>
                            </div>
                          </div>
                        )}

                        {/* Active Slot Count */}
                        {user.appMeta.activeSlotCount !== undefined && user.appMeta.activeSlotCount !== null && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl border border-teal-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <rect x="4" y="4" width="16" height="16" rx="2" />
                                <path d="M8 2v4M16 2v4M2 10h20" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-teal-700 uppercase tracking-wide">
                                Active Slots
                              </span>
                              <div className="text-lg font-bold text-teal-900">{user.appMeta.activeSlotCount}</div>
                            </div>
                          </div>
                        )}

                        {/* Spoken Languages Codes */}
                        {user.appMeta.spokenLangsCodes && user.appMeta.spokenLangsCodes.length > 0 && (
                          <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-2xl border border-pink-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-pink-700 uppercase tracking-wide">
                                Spoken Languages
                              </span>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {user.appMeta.spokenLangsCodes
                                  .filter((langCode: string) => langCode)
                                  .map((langCode: string, idx: number) => (
                                    <span
                                      key={langCode + idx}
                                      className="px-3 py-1 bg-pink-100 text-pink-800 text-sm font-semibold rounded-full border border-pink-200"
                                      title={`${langCode}: ${getLangNameByLangCode(langCode)}`}
                                    >
                                      {getLangNameByLangCode(langCode)}
                                      <span className="ml-1 text-xs text-pink-600 font-mono">({langCode})</span>
                                    </span>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left column */}
                    <div className="space-y-6">
                      {user.appProfile.gender && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-blue-700 uppercase tracking-wide">Gender</span>
                            <div className="text-lg font-bold text-blue-900">{user.appProfile.gender}</div>
                          </div>
                        </div>
                      )}

                      {/* Native Language */}
                      {Array.isArray(user.appProfile.nativeLangCode) && user.appProfile.nativeLangCode.length > 0 && (
                        <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-2xl border border-indigo-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 20l4-16M12 20l-4-16" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-indigo-700 uppercase tracking-wide">
                              Native Languages
                            </span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {user.appProfile.nativeLangCode
                                .filter((lang: string) => lang)
                                .map((lang: string, idx: number) => (
                                  <span
                                    key={lang + idx}
                                    className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm font-semibold rounded-full border border-indigo-200"
                                    title={`${lang}: ${getLangNameByLangCode(lang)}`}
                                  >
                                    {getLangNameByLangCode(lang)}
                                    <span className="ml-1 text-xs text-indigo-600 font-mono">({lang})</span>
                                  </span>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Spoken Languages */}
                      {Array.isArray(user.appProfile.spokenLanguages) && user.appProfile.spokenLanguages.length > 0 && (
                        <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-2xl border border-cyan-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-cyan-700 uppercase tracking-wide">
                              Spoken Languages
                            </span>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {user.appProfile.spokenLanguages
                                .filter((lang: any) => lang && lang.langCode)
                                .map((lang: any) => (
                                  <span
                                    key={lang.langCode}
                                    className="px-3 py-1 bg-cyan-100 text-cyan-800 text-sm font-semibold rounded-full border border-cyan-200"
                                    title={`${lang.langCode}: ${getLangNameByLangCode(lang.langCode)}`}
                                  >
                                    {getLangNameByLangCode(lang.langCode)}
                                    <span className="ml-1 text-xs text-cyan-600 font-mono">({lang.langCode})</span>
                                    {lang?.level && (
                                      <span className="ml-2 text-xs text-cyan-700 font-normal">({lang.level})</span>
                                    )}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {user.appProfile.invitationCode && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl border border-purple-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M16 7a4 4 0 01-8 0" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-purple-700 uppercase tracking-wide">
                              Invitation Code
                            </span>
                            <div className="text-lg font-bold text-purple-900">{user.appProfile.invitationCode}</div>
                          </div>
                        </div>
                      )}

                      {user.appProfile.academicBackground && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-2xl border border-green-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 20v-6m0 0V4m0 10l6 6m-6-6l-6 6" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-green-700 uppercase tracking-wide">
                              Academic Background
                            </span>
                            <div className="text-lg font-bold text-green-900">{user.appProfile.academicBackground}</div>
                          </div>
                        </div>
                      )}

                      {user.organizationId && user.projectId && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl border border-teal-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M3 7v4a1 1 0 001 1h3v2a1 1 0 001 1h4a1 1 0 001-1v-2h3a1 1 0 001-1V7" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-teal-700 uppercase tracking-wide">
                              Organization User
                            </span>
                            <div className="text-lg font-bold text-teal-900">{user.organizationId}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right column */}
                    <div className="space-y-6">
                      {user.appProfile.coachingSpentTimeInMin !== undefined &&
                        user.appProfile.coachingSpentTimeInMin !== null && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v6l4 2" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-yellow-700 uppercase tracking-wide">
                                Coaching Spent Time
                              </span>
                              <div className="text-lg font-bold text-yellow-900">
                                {Number(user.appProfile.coachingSpentTimeInMin).toFixed(2)} min
                              </div>
                            </div>
                          </div>
                        )}

                      {user.appProfile.learningTimeSpentInMin !== undefined &&
                        user.appProfile.learningTimeSpentInMin !== null && (
                          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl border border-teal-200">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                              <svg
                                className="w-6 h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <rect x="4" y="4" width="16" height="16" rx="2" />
                                <path d="M8 2v4M16 2v4M2 10h20" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium text-teal-700 uppercase tracking-wide">
                                Learning Spent Time
                              </span>
                              <div className="text-lg font-bold text-teal-900">
                                {Number(user.appProfile.learningTimeSpentInMin).toFixed(2)} min
                              </div>
                            </div>
                          </div>
                        )}

                      {user.appProfile.followersCount !== undefined && user.appProfile.followersCount !== null && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl border border-red-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-red-700 uppercase tracking-wide">Followers</span>
                            <div className="text-lg font-bold text-red-900">{user.appProfile.followersCount}</div>
                          </div>
                        </div>
                      )}

                      {user.appProfile.followingCount !== undefined && user.appProfile.followingCount !== null && (
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-2xl border border-pink-200">
                          <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg
                              className="w-6 h-6 text-white"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 20l9-5-9-5-9 5 9 5z" />
                              <path d="M12 12V4" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-pink-700 uppercase tracking-wide">Following</span>
                            <div className="text-lg font-bold text-pink-900">{user.appProfile.followingCount}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Learning Space Modal */}
      {hasLearnerRole && user?.id && (
        <Modal
          isOpen={showAddLearningSpaceModal}
          onClose={() => !addLearningSpaceSubmitting && setShowAddLearningSpaceModal(false)}
          size={2}
          hideFooter
        >
          <div className="p-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <FaBook className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Add New Learning Space</h3>
                <p className="text-sm text-gray-500 mt-0.5">Choose language and optional level</p>
              </div>
            </div>
            <div className="space-y-5">
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Language <span className="text-red-500 font-normal">*</span>
              </label>
              <select
                value={addLearningSpaceLangCode}
                onChange={(e) => setAddLearningSpaceLangCode(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 text-gray-900 font-medium transition-all duration-200 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/20 focus:outline-none hover:border-gray-300 hover:bg-white appearance-none cursor-pointer shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "1.25rem",
                  paddingRight: "2.5rem",
                }}
              >
                <option value="">Select language</option>
                {languageTuples.map(([code, name]) => (
                  <option key={code} value={code}>{name} ({code})</option>
                ))}
              </select>
            </div>
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Level <span className="text-gray-400 font-normal text-xs normal-case">(optional)</span>
              </label>
              <select
                value={addLearningSpaceLangLevel}
                onChange={(e) => setAddLearningSpaceLangLevel(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 text-gray-900 font-medium transition-all duration-200 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/20 focus:outline-none hover:border-gray-300 hover:bg-white appearance-none cursor-pointer shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "1.25rem",
                  paddingRight: "2.5rem",
                }}
              >
                <option value="">None</option>
                {LANG_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-200">
            <button
              type="button"
              onClick={() => !addLearningSpaceSubmitting && setShowAddLearningSpaceModal(false)}
              disabled={addLearningSpaceSubmitting}
              className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddLearningSpace}
              disabled={addLearningSpaceSubmitting || !addLearningSpaceLangCode.trim()}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {addLearningSpaceSubmitting ? "Adding…" : "Add Learning Space"}
            </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-full shadow-2xl ring-4 ring-amber-300/80 ring-offset-2 ring-offset-white transition-all duration-300 z-50 animate__animated animate__fadeInUp group"
          aria-label="Scroll to top"
        >
          <div className="flex items-center justify-center rounded-full border-2 border-amber-100/80 px-3 py-3">
            <FaArrowUp className="text-xl group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
        </button>
      )}
    </div>
  );
};
